import { Client, Setting } from '../../business-domains/retail/shared/clients/src/lib/client/client.model';

import { Injectable, Injector } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, BehaviorSubject } from 'rxjs';
import { RoleService } from '../services/role.service';
import { UserPermission, RolePermissions } from '../models/role.model';
import { AuthService } from '@core/auth/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionsSubject = new BehaviorSubject<UserPermission[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();
  Settings: Setting[] = JSON.parse(localStorage.getItem('Settings') || '[]');
  ClientDetails: Client | null = JSON.parse(localStorage.getItem('clientdata') || 'null');

  constructor(
    private ngxPermissionsService: NgxPermissionsService,
    private roleService: RoleService,
    private injector: Injector
  ) {
    //this.checkSetting();
  }

  /**
   * Load permissions for a specific role and set them in ngx-permissions
   */
  loadPermissionsForRole(roleId: number): Observable<any> {
    return new Observable(observer => {
      // Special handling for Super Admin (role 0)
      if (roleId === 0) {
        const superAdminPermissions = this.getSuperAdminPermissions();
        this.setPermissions(superAdminPermissions);
        this.permissionsSubject.next(superAdminPermissions);
        observer.next(superAdminPermissions);
        observer.complete();
        return;
      }

      // For other roles, fetch from API
      this.checkSetting();
      this.checkClientDetails();
      this.roleService.getRolePermissionMatrix(roleId).subscribe({
        next: (response: any) => {
          if (response.status === 200 && response.data) {
            const permissions = this.transformPermissions(response.data);
            this.setPermissions(permissions);
            this.permissionsSubject.next(permissions);
            observer.next(permissions);
            observer.complete();
          } else {
            observer.error('Failed to load permissions');
          }
        },
        error: (error: any) => {
          console.error('Error loading permissions:', error);
          observer.error(error);
        }
      });
    });
  }

  private getSuperAdminPermissions(): UserPermission[] {
    const allMenus = [
      'Client',
      'User Management',
      'Product List',
      'Brands',
      'Category',
      'Subcategory',
      'Product Variants',
      'Units',
      'Warehouse',
      'Store',
      // 'Barcodes',
      'Inventory',
      'Suppliers',
      'Customers',
      'Add Purchase Entry',
      'Purchase Entry List',
      'Purchase Return List',
      'Stock Transfer',
      'Offers & Combos',
      'Payment Due Status',
      'Bank Accounts List',
      'Account Transaction',
      'Sales Report',
      'Sales Return Report',
      'Purchase Report',
      'Purchase Return Report',
      'GST - Sales',
      'GST - Purchase',
      'Selling Price Group',
      'Sales',
      'Sales Return',
      'Loyalty Membership',
      'Loyalty Points',
      'Bill Counters',
      'Receivable - Due List',
      'Stock Adjustment'
    ];

    return allMenus.map(menu => ({
      menu,
      create: true,
      read: true,
      update: true,
      delete: true
    }));
  }

  /**
   * Transform API response to UserPermission format
   */
  private transformPermissions(rolePermissions: RolePermissions[]): UserPermission[] {
    return rolePermissions.map(rp => ({
      menu: rp.menu,
      create: rp.create === 1,
      read: rp.read === 1,
      update: rp.update === 1,
      delete: rp.delete === 1
    }));
  }

  /**
   * Set permissions in ngx-permissions service
   */
  private setPermissions(permissions: UserPermission[]): void {
    // Clear existing permissions
    this.ngxPermissionsService.flushPermissions();

    // Add new permissions
    permissions.forEach(permission => {
      const permissionName = permission.menu.toLowerCase().replace(/\s+/g, '-');

      // Add menu-level permission if user has any CRUD permission
      const hasAnyPermission = permission.create || permission.read || permission.update || permission.delete;
      if (hasAnyPermission) {
        this.ngxPermissionsService.addPermission(permissionName);
      }

      // Add action-level permissions
      if (permission.create) {
        this.ngxPermissionsService.addPermission(`${permissionName}-create`);
      }
      if (permission.read) {
        this.ngxPermissionsService.addPermission(`${permissionName}-read`);
      }
      if (permission.update) {
        this.ngxPermissionsService.addPermission(`${permissionName}-update`);
      }
      if (permission.delete) {
        this.ngxPermissionsService.addPermission(`${permissionName}-delete`);
      }
    });

    // Permissions are now set in ngx-permissions service
  }

  /**
   * Check if user has any permission (create, read, update, delete) for a specific menu
   */
  hasMenuPermission(menuName: string): boolean {
    const permissions = this.getCurrentPermissions();
    const permission = permissions.find(p => p.menu === menuName);
    const authService = this.injector.get(AuthService);
    const roleID = authService.getCurrentUserRole();
  //  console.log(permission)

    if (permission && (permission?.menu == 'Brands' || permission?.menu == 'Category' || permission?.menu == 'Subcategory'))
      return this.getSettingval(permission?.menu.toLocaleLowerCase()) && (permission.create || permission.read || permission.update || permission.delete)
    else if (permission && permission?.menu == 'Offers & Combos')
      return this.getSettingval('subcategory') && (permission.create || permission.read || permission.update || permission.delete)
    else if (permission && (permission?.menu == 'Loyalty Points' || permission?.menu == 'Loyalty Membership')) {
      return this.getSettingval('rewardpoint') && (permission.create || permission.read || permission.update || permission.delete)
    }

    if ((permission && (permission.menu == 'Warehouse' || permission.menu == 'Stock Transfer'))) {// && roleID == 1
      // //console.log(this.ClientDetails?.licensetype)
      if (this.ClientDetails?.licensetype == 'Single Store')
        return false
      else
        return true && (permission.create || permission.read || permission.update || permission.delete)
    }

    if (permission && permission?.menu == 'Add Purchase Entry')
      return permission.create

    if (permission && permission?.menu == 'Payment Due Status' || permission?.menu == 'Receivable - Due List') {
      ////console.log('Permission for', permission.menu, permission.update);
      return permission.update
    }

    if (permission && permission?.menu == 'Settings')
      return permission.create || permission.update

    if (!permission) {
      return false;
    }

    // Return true if user has ANY permission for this menu
    return permission.create || permission.read || permission.update || permission.delete;
  }

  getSettingval(field: string): boolean {
    //alert('ok')
    const setting = this.Settings.find(s => s.field === field && s.status == 1);
    ////console.log(setting)
    ////console.log('Setting for', field, setting?.value);
    return setting ? setting.value === 1 : false;
  }

  /**
   * Check if user has a specific action permission for a menu
   */
  hasSpecificPermission(menuName: string, action: 'create' | 'read' | 'update' | 'delete'): boolean {
    const permissions = this.getCurrentPermissions();
    const permission = permissions.find(p => p.menu === menuName);

    if (!permission) {
      return false;
    }

    return permission[action] || false;
  }

  /**
   * Get current permissions
   */
  getCurrentPermissions(): UserPermission[] {
    return this.permissionsSubject.value;
  }



  /**
   * Check if user has specific action permission
   */
  hasActionPermission(menuName: string, action: 'create' | 'read' | 'update' | 'delete'): boolean {
    const permissionName = menuName.toLowerCase().replace(/\s+/g, '-');
    return this.ngxPermissionsService.getPermission(`${permissionName}-${action}`) !== undefined;
  }

  //Check Client general settings for brands, category & subcategory
  checkSetting() {
    this.roleService.getGensettings().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.Settings = response.data;
          localStorage.setItem('Settings', JSON.stringify(this.Settings));
          console.log(this.Settings)
        }
      },
      error: (error: any) => {
        console.error('Error loading general settings:', error);
      }
    })
  }

  //Check Client license type for warehouse & STN
  checkClientDetails() {
    this.roleService.getClientDetail().subscribe({
      next: (response: any) => {
        // //console.log(response)
        if (response && response.data) {
          localStorage.setItem('clientdata', JSON.stringify(response.data));
          this.ClientDetails = response.data
          ////console.log(this.Settings)
        }
      },
      error: (error: any) => {
        console.error('Error loading general settings:', error);
      }
    })
  }


  /**
   * Clear all permissions
   */
  clearPermissions(): void {
    this.ngxPermissionsService.flushPermissions();
    this.permissionsSubject.next([]);
  }

  // canAccessRoute method removed - now using hasMenuPermission in permission guard for consistency


}
